import type { Permission, Role } from '@/types/auth';

/**
 * Stock-video bundle elevation — kept as a named special case, not
 * generalised as a framework concept. Stock video is the only asset kind
 * with this behaviour today; if a second instance appears we can refactor.
 *
 * "Pro" bundle: the user holds all four stock-video permissions (4k +
 * 1080p + unlimited + premium). They are effectively a Pro user for stock
 * video purposes regardless of their plan role.
 *
 * "Creator" bundle: the user holds 1080p + unlimited + premium, but not
 * 4k. They are effectively a Creator user.
 *
 * A partial grant outside these bundles (e.g. only `unlimited`) does not
 * elevate — the user falls back to their plan-role tier so credit and
 * premium assumptions stay consistent with the rest of the UX.
 *
 * Note: `download_stock_video_format_720p` is part of the permission
 * registry so it surfaces in the JWT, but it isn't consulted here — 720p
 * is available to every tier (Free included, via credits) so holding it
 * alone doesn't change anything.
 */
export type StockVideoBundle = 'pro' | 'creator' | null;

export type StockVideoEffectiveTier =
  | 'pro'
  | 'creator'
  | 'essentials'
  | 'free'
  | 'unknown';

export const computeStockVideoBundle = (
  permissions: ReadonlySet<Permission>
): StockVideoBundle => {
  const has4k = permissions.has('download_stock_video_format_4k');
  const has1080p = permissions.has('download_stock_video_format_1080p');
  const hasUnlimited = permissions.has('unlimited_downloads_stock_video');
  const hasPremium = permissions.has('download_premium_stock_video');

  if (has4k && has1080p && hasUnlimited && hasPremium) {
    return 'pro';
  }
  if (has1080p && hasUnlimited && hasPremium) {
    return 'creator';
  }
  return null;
};

const TIER_RANK: Record<StockVideoEffectiveTier, number> = {
  unknown: 0,
  free: 1,
  essentials: 2,
  creator: 3,
  pro: 4,
};

const tierForRoles = (roles: ReadonlySet<Role>): StockVideoEffectiveTier => {
  if (roles.has('Pro') || roles.has('Business')) {
    return 'pro';
  }
  if (roles.has('Premium') || roles.has('Creator')) {
    return 'creator';
  }
  if (roles.has('Essentials')) {
    return 'essentials';
  }
  if (roles.has('Free')) {
    return 'free';
  }
  return 'unknown';
};

/**
 * The higher of the permission bundle and the plan-role tier. The bundle can
 * only ever *elevate* — a Business/Pro subscriber whose JWT is missing
 * `download_stock_video_format_4k` reads as a "creator" bundle, and taking
 * that verbatim used to lock 4K for them (DEV-6677).
 */
export const computeStockVideoEffectiveTier = (
  bundle: StockVideoBundle,
  roles: ReadonlySet<Role>
): StockVideoEffectiveTier => {
  const roleTier = tierForRoles(roles);

  return bundle && TIER_RANK[bundle] > TIER_RANK[roleTier] ? bundle : roleTier;
};
